import { Global } from '@emotion/react';
import type { IntrinsicSystemProps } from '@hover/blueprint';
import { Box, VStack, transition, useTheme } from '@hover/blueprint';
import { breakpoints } from '@hover/blueprint/foundation';
import { includes } from '@hover/utilities';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import { FloatingNavOverlay } from 'src/modules/homeownerApp/components/Layout/FloatingNavOverlay';
import { SetIntercomVisibility } from 'src/shared/components/SetIntercomVisibility';
import { Web } from 'src/shared/constants/FeatureFlag';
import {
  useCurrentPage,
  useIsEmbed,
  useReactNativeWebView,
  useTreatment,
} from 'src/shared/hooks';
import { WebViewScope } from 'src/shared/hooks/useReactNativeWebView';
import type { CurrentPage } from 'src/shared/hooks/useSegmentHistory';
import { Route } from 'src/shared/route';
import { isEnabled } from 'src/shared/telemetry/featureFlag';

import { useHomeownerAppContext } from '../../hooks/useHomeownerAppContext';
import { SaveToProjectProvider, ViewportSizeProvider } from '../../providers';
import { HomeownerAppProvider } from '../../providers/HomeownerAppProvider';
import { HomeStylesProvider } from '../Home/HomeStyles';
import { Navbar } from '../Navbar';
import { SheetMenu } from '../SheetMenu';
import { UploadHomeownerImage } from '../UploadImage/UploadHomeownerImage';

export interface LayoutContainerProps extends IntrinsicSystemProps<'div'> {
  contentProps?: IntrinsicSystemProps<'div'>;
}

const navbarExclusionRoutes = [
  Route.HOMEOWNER_APP_SHARE.page,
  Route.HOMEOWNER_APP_CUSTOMIZE_UPLOAD.page,
  Route.HOMEOWNER_APP_PROJECT.page,
  Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.page,
];

const floatingExclusionRoutes = [
  Route.HOMEOWNER_APP_SHARE.page,
  Route.HOMEOWNER_APP_CUSTOMIZE_UPLOAD.page,
  Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.page,
  Route.HOMEOWNER_APP_ORIGINAL_DISPLAY_FULLSCREEN.page,
  Route.HOMEOWNER_APP_ORIGINAL.page,
];

const LayoutContainer = ({
  children,
  contentProps,
  ...props
}: LayoutContainerProps) => {
  const page = useCurrentPage();
  const { loggedIn } = useHomeownerAppContext();
  const isNewProjectUiEnabled = isEnabled(useTreatment(Web.NEW_PROJECT_UI));

  const fullBleedPages: CurrentPage[] = [
    Route.HOMEOWNER_APP_PROJECT.page,
    Route.HOMEOWNER_APP_HOME.page,
  ];
  const fullBleedPage = !!page && fullBleedPages.includes(page);
  const { colors, sizes, space } = useTheme();

  const isLoggedOutHome = page === Route.HOMEOWNER_APP_HOME.page && !loggedIn;
  const isLoggedOutFavorites =
    page === Route.HOMEOWNER_APP_SAVED.page && !loggedIn;
  const isLoggedOutResultsPage =
    page === Route.HOMEOWNER_APP_ORIGINAL.page && !loggedIn;

  const [isWebView] = useReactNativeWebView(WebViewScope.MOBILE_HOMEOWNER);
  const isEmbed = useIsEmbed();

  const isNavExcluded = includes(
    isNewProjectUiEnabled ? floatingExclusionRoutes : navbarExclusionRoutes,
    page ?? undefined,
  );

  const showNavbar =
    !isEmbed &&
    !isNavExcluded &&
    !isLoggedOutResultsPage &&
    !isWebView &&
    !isLoggedOutHome;

  return (
    <VStack
      alignItems="stretch"
      margin="0 auto"
      maxWidth={fullBleedPage ? {} : breakpoints.width500}
      spacing="0"
      {...props}
    >
      <SetIntercomVisibility visible={false} />
      <Global
        styles={{
          body: {
            background: colors.neutral[0],
            ...transition.easing500('background'),
          },
          'html,body': {
            overflowX: isLoggedOutFavorites ? 'hidden' : 'unset',
          },
          'ul[id^="chakra-toast-manager-"]': {
            padding: `${sizes[300]} !important`,
            margin: '0 !important',
            gap: space[200],
          },
        }}
      />
      <AnimatePresence initial={false}>
        {showNavbar && !isNewProjectUiEnabled && (
          <Navbar
            alignSelf="center"
            animate={{ opacity: 1 }}
            background="transparent"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            isSticky={!isLoggedOutHome}
            key="navbar"
            mode={isLoggedOutFavorites ? 'dark' : 'light'}
          />
        )}
      </AnimatePresence>
      {showNavbar && isNewProjectUiEnabled && !isLoggedOutHome && (
        <FloatingNavOverlay />
      )}
      <Box
        className="homeowner-layout-content"
        display="block"
        position="relative"
        {...contentProps}
      >
        {children}
      </Box>
      <UploadHomeownerImage />
    </VStack>
  );
};

const Layout = (props: LayoutContainerProps) => (
  <ViewportSizeProvider>
    <HomeownerAppProvider>
      <HomeStylesProvider>
        <SaveToProjectProvider>
          <LayoutContainer {...props} />
        </SaveToProjectProvider>
      </HomeStylesProvider>
    </HomeownerAppProvider>
  </ViewportSizeProvider>
);

/*
 * The RouteAwareLayout _must_ be mounted as a child of the deepest <Route>
 * component to ensure hooks like useParams use the correct route. Failure to
 * mount this component as a child of the deepest <Route> results in useParams
 * and other hooks to use the route matched in
 * `src/modules/app/components/Router/index.tsx` which results in params not
 * getting parsed.
 */
const RouteAwareLayout = () => <SheetMenu />;

export { Layout, RouteAwareLayout };
